import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fetchPdfBlob } from "@/lib/printPdf";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { formatDate } from "@/lib/constants";
import { ChangeDialog, DispatchDialog, ResponseDialog, ReqStatus, TrackingInfo, normStatus, rentLine } from "./RequestWorkflow";
import { RequestTimeline, LinkDialog, LawyerDialog } from "./RequestCase";
import ConsentReview from "./ConsentReview";

const openDoc = async (id) => {
  try { const { blob } = await fetchPdfBlob(`/documents/${id}/download`); window.open(URL.createObjectURL(blob), "_blank"); }
  catch (e) { toast.error(e.message); }
};
const Btn = ({ children, onClick, testId, tone = "teal" }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded px-2 py-0.5 border text-[11px] font-medium ${tone === "primary" ? "bg-emerald-600 text-white border-emerald-600" : tone === "danger" ? "border-rose-200 text-rose-700 hover:bg-rose-50" : "border-teal-200 text-teal-800 hover:bg-teal-50"}`}>{children}</button>
);

const NEXT = {
  draft: "Entwurf prüfen und drucken/versenden – oder zur Freigabe vorlegen",
  pending_approval: "Wartet auf Freigabe durch Objektbetreuer/Geschäftsführung",
  approved: "Freigegeben – Schreiben versenden und Versand dokumentieren",
  sent: "Versendet – Zustimmung des Mieters abwarten",
  consent_received: "Zustimmung eingegangen – Unterschrift prüfen",
  agreed: "Zugestimmt – neue Miete wird zum Stichtag übernommen",
  partially_agreed: "Teilweise zugestimmt – Rest ggf. einklagen",
  rejected: "Abgelehnt – Mieter erneut anschreiben oder Klage prüfen (Akte für Anwalt)",
  applied: "Abgeschlossen – neue Miete ist wirksam",
  cancelled: "Storniert",
};

function NextStep({ r, isAdmin }) {
  const s = normStatus(r.status);
  const expired = s === "sent" && r.consent_deadline && r.consent_deadline < new Date().toISOString().slice(0, 10);
  const txt = r.superseded_by ? (s === "cancelled" ? "Durch Änderungsschreiben ersetzt/reduziert – siehe dort" : "Folge-/Änderungsschreiben angelegt – siehe dort") : expired ? "Zustimmungsfrist abgelaufen – Mieter erneut anschreiben oder Klage prüfen (Akte für Anwalt)" : NEXT[s];
  const warn = expired || ["pending_approval", "consent_received", "rejected"].includes(s);
  return (
    <div className={`text-xs rounded px-2 py-1 ${warn ? "bg-amber-50 text-amber-900 border border-amber-200" : "bg-teal-50 text-teal-900 border border-teal-100"}`} data-testid={`ri-request-next-${r.id}`}>
      <b>Nächster Schritt:</b> {txt}{["draft", "pending_approval"].includes(s) && !isAdmin ? " (Drucken/Versenden erst nach Freigabe)" : ""}
    </div>
  );
}

function KeyFacts({ r }) {
  const f = [["Schreiben vom", formatDate(r.letter_date)], ["Versand", r.dispatch ? formatDate(r.dispatch.sent_date) : "—"],
    ["Zugang", r.dispatch && !r.dispatch.delivered_assumed ? formatDate(r.dispatch.delivered_date) : "—"],
    ["Zustimmung bis", formatDate(r.consent_deadline)], ["Wirksam ab", formatDate(r.effective_date)], ["Freigabe", r.approved_by_name || "—"]];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid={`ri-request-facts-${r.id}`}>
      {f.map(([k, v]) => <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="text-xs font-medium text-slate-900">{v || "—"}</div></div>)}
    </div>
  );
}

function ProofUpload({ r, onDone }) {
  const ref = useRef(null);
  const up = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/rent-increase/requests/${r.id}/proof`, fd); toast.success("Versand-/Zugangsnachweis abgelegt"); onDone?.(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Upload fehlgeschlagen"); }
  };
  return (<>
    <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={up} data-testid={`ri-req-proof-input-${r.id}`} />
    <Btn onClick={() => ref.current?.click()} testId={`ri-req-proof-${r.id}`}>Versandnachweis hinzufügen</Btn>
  </>);
}

const printFollowup = async (r, reload) => {
  const w = window.open("", "_blank");
  try {
    const res = await api.post(`/rent-increase/requests/${r.id}/followup/print`, {}, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (w) w.location.href = url; else window.open(url, "_blank");
    toast.success(`${r.letter_kind === "amendment" ? "Änderungsschreiben" : "Folgeschreiben"} inkl. neuer Rückantwort erstellt und in der Akte abgelegt`);
    reload();
  } catch (e) {
    w?.close();
    const t = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e?.response?.data?.detail;
    toast.error(t || "Folgeschreiben konnte nicht erstellt werden");
  }
};

const reminder = async (r, reload) => {
  try {
    const { data } = await api.post(`/rent-increase/requests/${r.id}/reminder`);
    toast.success("Erinnerung inkl. Rückantwort erstellt – bitte drucken und versenden");
    openDoc(data.document_id); reload();
  } catch (e) { toast.error(e?.response?.data?.detail || "Erinnerung fehlgeschlagen"); }
};

const followup = async (r, reload) => {
  if (!window.confirm("Mieter erneut anschreiben? Es wird ein Folgeschreiben mit neuer Rückantwort, neuem QR-Code und neuer Zustimmungsfrist im selben Vorgang angelegt (Freigabe wie gewohnt).")) return;
  try {
    const { data } = await api.post(`/rent-increase/requests/${r.id}/followup`);
    toast.success(`Folgeschreiben Az. ${data.case_number} angelegt`); reload();
  } catch (e) { toast.error(e?.response?.data?.detail || "Folgeschreiben fehlgeschlagen"); }
};

function Actions({ r, isAdmin, act, setDlg, openDraft }) {
  const s = normStatus(r.status);
  const expired = s === "sent" && r.consent_deadline && r.consent_deadline < new Date().toISOString().slice(0, 10);
  const isFollowup = ["followup", "amendment"].includes(r.letter_kind);
  const printLabel = r.letter_kind === "amendment" ? "Änderungsschreiben drucken" : "Folgeschreiben drucken";
  return (
    <span className="flex flex-wrap gap-1.5">
      {!isFollowup && (s === "draft" || (r.draft_id && ["pending_approval", "approved"].includes(s))) && <Btn tone="primary" onClick={() => openDraft(r)} testId={`ri-req-open-draft-${r.id}`}>Entwurf öffnen</Btn>}
      {isFollowup && (s === "approved" || (["draft", "pending_approval"].includes(s) && isAdmin)) && <Btn tone="primary" onClick={() => printFollowup(r, act.reload)} testId={`ri-req-print-followup-${r.id}`}>{printLabel}</Btn>}
      {(["draft", "pending_approval", "approved"].includes(s) || (s === "sent" && !r.superseded_by)) && <Btn onClick={() => setDlg({ kind: "change", r })} testId={`ri-req-change-${r.id}`}>Ändern</Btn>}
      {s === "sent" && !expired && <Btn onClick={() => reminder(r, act.reload)} testId={`ri-req-reminder-${r.id}`}>Erinnerung erzeugen</Btn>}
      {(s === "rejected" || expired) && !r.superseded_by && <Btn tone="primary" onClick={() => followup(r, act.reload)} testId={`ri-req-followup-${r.id}`}>Mieter erneut anschreiben</Btn>}
      {s === "draft" && !isAdmin && <Btn onClick={() => act(r, "submit")} testId={`ri-req-submit-${r.id}`}>Zur Freigabe vorlegen</Btn>}
      {["draft", "pending_approval"].includes(s) && isAdmin && <Btn onClick={() => act(r, "approve", { approve: true })} testId={`ri-req-approve-${r.id}`}>Freigeben</Btn>}
      {s === "pending_approval" && isAdmin && <Btn tone="danger" onClick={() => act(r, "approve", { approve: false, note: window.prompt("Grund für die Ablehnung der Freigabe:") || "" })} testId={`ri-req-deny-${r.id}`}>Zurückweisen</Btn>}
      {(s === "approved" || (["draft", "pending_approval"].includes(s) && isAdmin)) && <Btn tone="primary" onClick={() => setDlg({ kind: "dispatch", r })} testId={`ri-req-dispatch-${r.id}`}>Versand dokumentieren</Btn>}
      {["sent", "consent_received", "agreed", "partially_agreed", "rejected"].includes(s) && <ProofUpload r={r} onDone={act.reload} />}
      {s === "sent" && <Btn tone="primary" onClick={() => setDlg({ kind: "response", r })} testId={`ri-req-response-${r.id}`}>Antwort erfassen</Btn>}
      {(s === "rejected" || expired) && <Btn onClick={() => setDlg({ kind: "lawyer", r })} testId={`ri-req-lawyer-${r.id}`}>Akte für Anwalt</Btn>}
      {s !== "cancelled" && <Btn onClick={() => act(r, "notify-owner", {})} testId={`ri-req-owner-${r.id}`}>Eigentümer informieren</Btn>}
      {s !== "cancelled" && <Btn onClick={() => setDlg({ kind: "link", r })} testId={`ri-req-link-${r.id}`}>Dokument/Schriftverkehr zuordnen</Btn>}
      {s === "rejected" && <Btn onClick={() => act(r, "close", { note: "ohne Klage" })} testId={`ri-req-close-${r.id}`}>Ohne Klage abschließen</Btn>}
      {!["applied", "cancelled"].includes(s) && <Btn tone="danger" onClick={() => window.confirm("Mieterhöhung stornieren?") && act(r, "cancel", {})} testId={`ri-req-cancel-${r.id}`}>Stornieren</Btn>}
      {s === "cancelled" && <Btn tone="danger" onClick={() => window.confirm("Stornierte Mieterhöhung endgültig löschen? Alle zugehörigen Schreiben, Rückantworten, Scans, Versandbelege, Journal-Einträge und der Vorgang werden entfernt.") && act(r, "__delete")} testId={`ri-req-delete-${r.id}`}>Löschen</Btn>}
    </span>
  );
}

const STEPS = [["Entwurf", ["draft"]], ["Freigabe", ["pending_approval", "approved"]], ["Versand", ["sent"]],
  ["Zustimmung", ["consent_received", "agreed", "partially_agreed", "rejected"]], ["Wirksam", ["applied"]]];
const BAR = { cancelled: "bg-slate-300", rejected: "bg-rose-500", applied: "bg-emerald-600", consent_received: "bg-emerald-400", agreed: "bg-emerald-500", partially_agreed: "bg-teal-500", sent: "bg-indigo-500", approved: "bg-sky-500", pending_approval: "bg-amber-500", draft: "bg-slate-400" };

function Steps({ status, id }) {
  const s = normStatus(status);
  const cur = STEPS.findIndex(([, k]) => k.includes(s));
  if (s === "cancelled") return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px]" data-testid={`ri-request-steps-${id}`}>
      {STEPS.map(([l], i) => (
        <span key={l} className="flex items-center gap-1">
          <span className={`rounded-full px-1.5 py-0.5 ${i < cur ? "bg-emerald-100 text-emerald-800" : i === cur ? (s === "rejected" ? "bg-rose-600 text-white" : "bg-slate-900 text-white") : "bg-slate-100 text-slate-400"}`}>{i + 1} {l}</span>
          {i < STEPS.length - 1 && <span className="text-slate-300">›</span>}
        </span>
      ))}
    </div>
  );
}

function CaseHeader({ r, nr, isOpen, onToggle }) {
  const s = normStatus(r.status);
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-1.5">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Mieterhöhung {nr} · {r.letter_kind === "followup" ? <span className="text-amber-700" data-testid={`ri-request-followup-badge-${r.id}`}>Folgeschreiben zu Az. {r.previous?.case_number} · </span> : r.letter_kind === "amendment" ? <span className="text-amber-700" data-testid={`ri-request-amendment-badge-${r.id}`}>Änderungsschreiben ({r.amendment_mode === "reduce" ? "Reduzierung" : "Ersetzung"}) zu Az. {r.previous?.case_number} · </span> : null}Schreiben vom {formatDate(r.letter_date)}</div>
        {r.case_number
          ? <Link to={r.vorgang_id ? `/vorgaenge?id=${r.vorgang_id}` : "/vorgaenge"} className="font-mono text-base font-semibold text-slate-900 hover:text-teal-700 hover:underline" title="Vorgang öffnen" data-testid={`ri-request-vorgang-${r.id}`}>Az. {r.case_number}</Link>
          : <span className="font-mono text-base font-semibold text-slate-400" data-testid={`ri-request-vorgang-${r.id}`}>Az. wird bei Freigabe vergeben</span>}
      </div>
      <div className={`text-sm tabular-nums font-medium ${s === "cancelled" ? "line-through text-slate-400" : "text-slate-900"} pt-3`}>{rentLine(r)}</div>
      <div className="ml-auto flex items-center gap-2 pt-2.5">
        <ReqStatus status={r.status} testId={`ri-request-status-${r.id}`} />
        <button type="button" className="text-[11px] text-teal-700 hover:underline" onClick={onToggle} data-testid={`ri-request-toggle-${r.id}`}>{isOpen ? "Akte ausblenden" : "Akte & Verlauf"}</button>
      </div>
    </div>
  );
}

export default function RequestsList({ tenancyId, reloadKey, onApplied, onOpenDraft }) {
  const [rows, setRows] = useState([]);
  const isAdmin = !!rows[0]?.can_approve;  // GF/Admin oder Objektbetreuer
  const [open, setOpen] = useState(null);
  useEffect(() => { if (rows.length && open === null) setOpen(rows[0].id); }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dlg, setDlg] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const load = () => api.get(`/rent-increase/tenancy/${tenancyId}/requests`).then(({ data }) => setRows(data.requests));
  useEffect(() => { load(); }, [tenancyId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tick, setTick] = useState(0);
  const act = async (r, action, body = {}) => {
    try {
      if (action === "__delete") await api.delete(`/rent-increase/requests/${r.id}`);
      else await api.post(`/rent-increase/requests/${r.id}/${action}`, body); toast.success(action === "notify-owner" ? "Eigentümer per E-Mail informiert" : "Gespeichert"); load(); setTick((x) => x + 1); onApplied?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };
  act.reload = () => { load(); setTick((x) => x + 1); };
  const openDraft = async (r) => {
    try {
      if (r.draft_id) {
        const ok = await api.get(`/messages/drafts/${r.draft_id}`).then(() => true).catch(() => false);
        if (ok) return onOpenDraft?.({ draft_id: r.draft_id, request_id: r.id, category: "Mieterhöhung", follow_up_date: r.consent_deadline, my_reference: r.case_number, attachment_document_ids: r.consent_form_document_id ? [r.consent_form_document_id] : [] });
      }
      const { data } = await api.post(`/rent-increase/tenancy/${tenancyId}/editor`);
      onOpenDraft?.(data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Entwurf konnte nicht geöffnet werden"); }
  };
  if (!rows.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2.5" data-testid="ri-requests">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">Mieterhöhungs-Vorgänge & Historie ({rows.length})</div>
      {(showAll ? rows : rows.slice(0, 10)).map((r, idx) => (
        <div key={r.id} className={`relative overflow-hidden rounded-md border bg-white pl-4 pr-3 py-2.5 space-y-1.5 ${open === r.id ? "border-slate-300 shadow-sm" : "border-slate-200"} ${normStatus(r.status) === "cancelled" ? "opacity-70" : ""}`} data-testid={`ri-request-${r.id}`}>
          <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${BAR[normStatus(r.status)] || "bg-slate-300"}`} />
          <CaseHeader r={r} nr={rows.length - idx} isOpen={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />
          <Steps status={r.status} id={r.id} />
          <NextStep r={r} isAdmin={isAdmin} />
          {normStatus(r.status) === "consent_received" && r.consent_document_id && <ConsentReview r={r} onDecide={(body) => act(r, "review-consent", body)} />}
          <KeyFacts r={r} />
          <TrackingInfo r={r} onChanged={load} />
          <div className="rounded bg-slate-50 border border-slate-100 px-2 py-1.5 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Aktionen</div>
            <Actions r={r} isAdmin={isAdmin} act={act} setDlg={setDlg} openDraft={openDraft} />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Unterlagen</span>
            {r.letter_document_id && <button type="button" className="text-teal-700 hover:underline" onClick={() => openDoc(r.letter_document_id)} data-testid={`ri-request-letter-${r.id}`}>Schreiben</button>}
            {r.consent_form_document_id && <button type="button" className="text-teal-700 hover:underline" onClick={() => openDoc(r.consent_form_document_id)}>Rückantwort-Formular</button>}
            {(r.proof_document_ids || []).map((d, i) => <button key={d} type="button" className="text-indigo-700 hover:underline" onClick={() => openDoc(d)} data-testid={`ri-request-proof-${r.id}-${i}`}>Versand-/Zugangsnachweis{i ? ` ${i + 1}` : ""}</button>)}
            {r.consent_document_id && <button type="button" className="text-emerald-700 font-medium hover:underline" onClick={() => openDoc(r.consent_document_id)} data-testid={`ri-request-consent-${r.id}`}>Unterschriebene Zustimmung</button>}
            {(r.reminder_document_ids || []).map((d, i) => <button key={d} type="button" className="text-amber-700 hover:underline" onClick={() => openDoc(d)} data-testid={`ri-request-reminder-doc-${r.id}-${i}`}>{i + 1}. Erinnerung</button>)}
            {r.vorgang_id && <Link to={`/vorgaenge?id=${r.vorgang_id}`} className="ml-auto text-teal-700 font-medium hover:underline" data-testid={`ri-request-open-vorgang-${r.id}`}>Vorgang/Akte öffnen →</Link>}
          </div>
          {open === r.id && <RequestTimeline rid={r.id} reloadKey={`${tick}-${reloadKey}`} />}
        </div>
      ))}
      {rows.length > 10 && <button type="button" className="text-xs text-teal-700 hover:underline" onClick={() => setShowAll((v) => !v)} data-testid="ri-requests-show-all">{showAll ? "Nur die letzten 10 anzeigen" : `Alle ${rows.length} Vorgänge anzeigen`}</button>}
      {dlg?.kind === "dispatch" && <DispatchDialog req={dlg.r} open onClose={() => setDlg(null)} onDone={load} />}
      {dlg?.kind === "link" && <LinkDialog req={dlg.r} tenancyId={tenancyId} onClose={() => setDlg(null)} onDone={() => { load(); setTick((x) => x + 1); }} />}
      {dlg?.kind === "lawyer" && <LawyerDialog req={dlg.r} onClose={() => setDlg(null)} onDone={() => { load(); setTick((x) => x + 1); }} />}
      {dlg?.kind === "change" && <ChangeDialog req={dlg.r} open onClose={() => setDlg(null)} onDone={() => { load(); setTick((x) => x + 1); }} />}
      {dlg?.kind === "response" && <ResponseDialog req={dlg.r} open onClose={() => setDlg(null)} onDone={() => { load(); onApplied?.(); }} />}
    </div>
  );
}
